import React, { useState, useEffect } from 'react';
import { Calendar, FileText, Clock, CheckCircle, XCircle, AlertCircle, RefreshCw, Info, Umbrella, Heart, X, Gift } from 'lucide-react';
import api from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { toast } from 'react-toastify';

const LeaveApplication = () => {
  const [formData, setFormData] = useState({
    startDate: '',
    endDate: '',
    reason: ''
  });
  const [leaveDays, setLeaveDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [leaves, setLeaves] = useState([]);
  const [leavesLoading, setLeavesLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('apply-leave');
  const [previousLeaves, setPreviousLeaves] = useState([]);
  const [autoRefreshing, setAutoRefreshing] = useState(false);
  const [leaveBalance, setLeaveBalance] = useState(null);

  useEffect(() => {
    fetchLeaves();
    fetchLeaveBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh leaves data every 30 seconds when on my-leaves tab
  useEffect(() => {
    if (activeTab === 'my-leaves') {
      const interval = setInterval(() => {
        setAutoRefreshing(true);
        fetchLeaves().finally(() => setAutoRefreshing(false));
        fetchLeaveBalance();
      }, 30000); // Refresh every 30 seconds

      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchLeaveBalance = async () => {
    try {
      const response = await api.get('/employee/leave-balance');
      setLeaveBalance(response.data);
    } catch (error) {
      console.error('Error fetching leave balance:', error);
    }
  };

  const fetchLeaves = async () => {
    setLeavesLoading(true);
    try {
      // Fetch ALL leaves without month/year filter, sorted by date descending
      const response = await api.get(`/employee/leave`);
      const newLeaves = response.data.leaves || [];
      
      // Check for status changes and show notifications
      if (previousLeaves.length > 0) {
        newLeaves.forEach(newLeave => {
          const oldLeave = previousLeaves.find(old => old._id === newLeave._id);
          if (oldLeave && oldLeave.status !== newLeave.status) {
            const startDate = new Date(newLeave.startDate).toLocaleDateString();
            const endDate = new Date(newLeave.endDate).toLocaleDateString();
            const dateRange = startDate === endDate ? startDate : `${startDate} to ${endDate}`;
            
            if (newLeave.status === 'approved') {
              toast.success(`Your leave for ${dateRange} has been approved!`);
            } else if (newLeave.status === 'rejected') {
              toast.error(`Your leave for ${dateRange} has been rejected.`);
            }
          }
        });
      }
      
      setLeaves(newLeaves);
      setPreviousLeaves(newLeaves);
    } catch (error) {
      console.error('Error fetching leaves:', error);
      toast.error('Failed to fetch leave applications');
    } finally {
      setLeavesLoading(false);
    }
  };

  const generateDatesArray = (start, end) => {
    const dates = [];
    let currentDate = new Date(start);
    const endDate = new Date(end);
    
    while (currentDate <= endDate) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
  };

  const isOnProbation = leaveBalance?.probationStatus === 'probation';
  const isSSBranch = leaveBalance?.branch === 'SSbranch';

  useEffect(() => {
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);

      if (start <= end) {
        const dates = generateDatesArray(start, end);
        const newLeaveDays = dates.map(date => ({
          date: date.toISOString().split('T')[0],
          dayType: 'full',
          leaveType: isOnProbation ? 'sick' : 'earned'
        }));
        setLeaveDays(newLeaveDays);
      }
    } else {
      setLeaveDays([]);
    }
  }, [formData.startDate, formData.endDate, isOnProbation]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleDayTypeChange = (index, dayType) => {
    if (leaveDays[index]?.leaveType === 'comp_off' && dayType !== 'full') {
      toast.info('Comp Off is available as a full-day paid leave only.');
      return;
    }
    const updatedDays = [...leaveDays];
    updatedDays[index].dayType = dayType;
    setLeaveDays(updatedDays);
  };

  const handleLeaveTypeChange = (index, leaveType) => {
    const updatedDays = [...leaveDays];
    updatedDays[index].leaveType = leaveType;
    if (leaveType === 'comp_off') updatedDays[index].dayType = 'full';
    setLeaveDays(updatedDays);
  };

  const calculateLeaveCount = () => {
    let earnedCount = 0;
    let sickCount = 0;
    let casualCount = 0;
    let compOffCount = 0;
    let withoutPayCount = 0;

    leaveDays.forEach(day => {
      const count = day.dayType === 'half' ? 0.5 : 1;
      if (day.leaveType === 'earned') {
        earnedCount += count;
      } else if (day.leaveType === 'sick') {
        sickCount += count;
      } else if (day.leaveType === 'casual') {
        casualCount += count;
      } else if (day.leaveType === 'comp_off') {
        compOffCount += count;
      } else if (day.leaveType === 'without_pay') {
        withoutPayCount += count;
      }
    });

    return { earnedCount, sickCount, casualCount, compOffCount, withoutPayCount };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Client-side validation
      if (!formData.startDate || !formData.endDate) {
        toast.error('Please select start and end dates');
        setLoading(false);
        return;
      }
      
      if (!formData.reason || formData.reason.trim().length < 10) {
        toast.error('Please provide a reason with at least 10 characters');
        setLoading(false);
        return;
      }

      if (leaveDays.length === 0) {
        toast.error('Please select valid date range');
        setLoading(false);
        return;
      }

      const compOffDays = leaveDays.filter((day) => day.leaveType === 'comp_off');
      if (compOffDays.length > 0 && (compOffDays.length !== leaveDays.length || compOffDays.length > (leaveBalance?.comp_off_left || 0))) {
        toast.error('Select only the available Comp Off balance when applying Comp Off.');
        setLoading(false);
        return;
      }

      // Convert dates to ISO format for backend
      const submitData = {
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString(),
        leaveDays: leaveDays.map(day => ({
          date: new Date(day.date).toISOString(),
          dayType: day.dayType,
          leaveType: day.leaveType
        })),
        reason: formData.reason
      };
      
      await api.post('/employee/leave', submitData);
      toast.success('Leave application submitted successfully!');
      setFormData({
        startDate: '',
        endDate: '',
        reason: ''
      });
      setLeaveDays([]);
      // Refresh the leaves list and balance after successful submission
      fetchLeaves();
      fetchLeaveBalance();
    } catch (error) {
      console.error('Leave application error:', error);
      console.error('Error response:', error.response?.data);
      
      const message = error.response?.data?.message || 'Failed to submit leave application';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'rejected':
        return <XCircle className="h-5 w-5 text-red-600" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-600" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      case 'pending':
        return 'Pending';
      default:
        return 'Unknown';
    }
  };

  const { earnedCount, sickCount, casualCount, compOffCount, withoutPayCount } = calculateLeaveCount();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Leave Application</h1>
        <p className="text-gray-600">Apply for leave and view your leave applications</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('apply-leave')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'apply-leave'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Calendar className="inline h-4 w-4 mr-2" />
              Apply for Leave
            </button>
            <button
              onClick={() => setActiveTab('my-leaves')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'my-leaves'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FileText className="inline h-4 w-4 mr-2" />
              My Leaves
            </button>
          </nav>
        </div>

        <div className="p-4">
          {/* My Leaves Tab */}
          {activeTab === 'my-leaves' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <h3 className="text-lg font-medium text-gray-900">My Leave Applications</h3>
                  {autoRefreshing && (
                    <span className="text-xs text-gray-500 flex items-center">
                      <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                      Auto-refreshing...
                    </span>
                  )}
                </div>
                <button
                  onClick={fetchLeaves}
                  disabled={leavesLoading}
                  className="flex items-center px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${leavesLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>

              {leavesLoading ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner size="lg" />
                </div>
              ) : leaves.length > 0 ? (
                <div className="space-y-4">
                  {leaves.map((leave) => (
                    <div key={leave._id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                          {/* Left Content */}
                          <div className="flex-1">
                            {/* Date Row */}
                            <div className="flex items-start justify-between md:block">
                              <div className="flex items-center space-x-3">
                                <Calendar className="h-5 w-5 text-blue-600 flex-shrink-0" />

                                <span className="text-lg font-medium text-gray-900">
                                  {new Date(leave.startDate).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  })}
                                  {new Date(leave.startDate).toDateString() !==
                                    new Date(leave.endDate).toDateString() && (
                                    <>
                                      {" "}
                                      to{" "}
                                      {new Date(leave.endDate).toLocaleDateString("en-US", {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                      })}
                                    </>
                                  )}
                                </span>
                              </div>

                              {/* Mobile Status */}
                              <div className="md:hidden ml-3">
                                <div
                                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                                    leave.status
                                  )}`}
                                >
                                  {getStatusIcon(leave.status)}
                                  <span className="ml-1">{getStatusText(leave.status)}</span>
                                </div>
                              </div>
                            </div>

                            {/* Days Breakdown */}
                            {leave.leaveDays && leave.leaveDays.length > 0 && (
                              <div className="mt-3">
                                <p className="text-xs font-semibold text-gray-600 uppercase mb-2">
                                  Days Breakdown:
                                </p>

                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                  {leave.leaveDays.map((day, index) => (
                                    <div
                                      key={index}
                                      className="flex items-center space-x-2 text-xs bg-gray-50 rounded px-2 py-1"
                                    >
                                      <span className="text-gray-600">
                                        {new Date(day.date).toLocaleDateString("en-US", {
                                          month: "short",
                                          day: "numeric",
                                        })}
                                      </span>

                                      <span
                                        className={`px-2 py-0.5 rounded ${
                                          day.dayType === "half"
                                            ? "bg-yellow-100 text-yellow-700"
                                            : "bg-blue-100 text-blue-700"
                                        }`}
                                      >
                                        {day.dayType === "half" ? "Half" : "Full"}
                                      </span>

                                      <span
                                        className={`px-2 py-0.5 rounded ${
                                          day.leaveType === "earned"
                                            ? "bg-green-100 text-green-700"
                                            : day.leaveType === "sick"
                                            ? "bg-pink-100 text-pink-700"
                                            : day.leaveType === "comp_off"
                                            ? "bg-amber-100 text-amber-700"
                                            : day.leaveType === "casual"
                                            ? "bg-purple-100 text-purple-700"
                                            : "bg-orange-100 text-orange-700"
                                        }`}
                                      >
                                        {day.leaveType === "earned"
                                          ? "Earned"
                                          : day.leaveType === "sick"
                                          ? "Sick"
                                          : day.leaveType === "comp_off"
                                          ? "Comp Off"
                                          : day.leaveType === "casual"
                                          ? "Casual"
                                          : "W/O Pay"}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Reason */}
                            <div className="mt-3">
                              <p className="text-sm text-gray-600">
                                <strong>Reason:</strong> {leave.reason}
                              </p>
                            </div>

                            {/* Applied Date */}
                            <div className="flex items-center space-x-4 text-sm text-gray-500 mt-2">
                              <span>Applied: {new Date(leave.createdAt).toLocaleDateString()}</span>
                            </div>
                          </div>

                          {/* Desktop Status */}
                          <div className="hidden md:block ml-4">
                            <div
                              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                                leave.status
                              )}`}
                            >
                              {getStatusIcon(leave.status)}
                              <span className="ml-1">{getStatusText(leave.status)}</span>
                            </div>
                          </div>
                        </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Calendar className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No leave applications</h3>
                  <p className="mt-1 text-sm text-gray-500">You haven't applied for any leaves yet.</p>
                  <div className="mt-6">
                    <button
                      onClick={() => setActiveTab('apply')}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                    >
                      <Calendar className="h-4 w-4 mr-2" />
                      Apply for Leave
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Apply Leave Tab */}
          {activeTab === 'apply-leave' && (
            <div className="space-y-6">
              {/* Info Box */}
              <div className={`border rounded-lg p-4 ${isOnProbation ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'}`}>
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <Info className={`h-6 w-6 ${isOnProbation ? 'text-amber-600' : 'text-blue-600'}`} />
                  </div>
                  <div className="flex-1">
                    <h4 className={`text-sm font-semibold mb-2 ${isOnProbation ? 'text-amber-900' : 'text-blue-900'}`}>How Leave Application Works</h4>
                    {isOnProbation ? (
                      <ul className="text-sm text-amber-800 space-y-1">
                        <li>Comp Off remains available when you work a full day on a Sunday, alternate Saturday, or listed holiday.</li>
                        <li>• You are currently on probation - only <strong>Sick Leave</strong> and <strong>Leave Without Pay</strong> can be applied for</li>
                        <li>• Earned Leave unlocks once HR confirms you</li>
                        <li>• Select start and end dates for your leave period</li>
                        <li>• Configure each day as <strong>Full Day</strong> or <strong>Half Day</strong></li>
                        <li>• Half-day leaves count as 0.5, full-day leaves count as 1.0</li>
                        <li>• Leave Without Pay doesn't require balance - salary will be deducted for these days</li>
                      </ul>
                    ) : (
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>Comp Off is earned from full-day work on Sundays, alternate Saturdays, and listed holidays and remains valid until the end of the financial year (31 March).</li>
                        <li>• Select start and end dates for your leave period</li>
                        <li>• Configure each day as <strong>Full Day</strong> or <strong>Half Day</strong></li>
                        <li>• Choose leave type: <strong>Earned Leave</strong> (carries forward), <strong>Sick Leave</strong>{isSSBranch ? <>, <strong>Casual Leave</strong></> : ''}, or <strong>Leave Without Pay</strong></li>
                        <li>• Half-day leaves count as 0.5, full-day leaves count as 1.0</li>
                        <li>• Leave Without Pay doesn't require balance - salary will be deducted for these days</li>
                        <li>• If you exceed your leave balance, use Leave Without Pay for remaining days</li>
                      </ul>
                    )}
                  </div>
                </div>
              </div>

              {/* Leave Balance Display */}
              {leaveBalance && (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                  {!isOnProbation && (
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-green-200 rounded-full">
                          <Umbrella className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-green-600">Earned Leaves Left</p>
                          <p className="text-3xl font-bold text-green-700">{leaveBalance.earned_leaves_left}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className={`bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg p-4 border border-pink-200 ${isOnProbation ? 'md:max-w-xs' : ''}`}>
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-pink-200 rounded-full">
                        <Heart className="h-5 w-5 text-pink-600" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-pink-600">Sick Leaves Left</p>
                        <p className="text-3xl font-bold text-pink-700">{leaveBalance.sick_leaves_left}</p>
                      </div>
                    </div>
                  </div>

                  {isSSBranch && !isOnProbation && (
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-purple-200 rounded-full">
                          <Calendar className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-purple-600">Casual Leaves Left</p>
                          <p className="text-3xl font-bold text-purple-700">{leaveBalance.casual_leaves_left}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-4 border border-amber-200">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-amber-200 rounded-full">
                        <Gift className="h-5 w-5 text-amber-700" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-amber-700">Comp Off Left (FY {leaveBalance.comp_off_financial_year || leaveBalance.comp_off_month})</p>
                        <p className="text-3xl font-bold text-amber-800">{leaveBalance.comp_off_left}</p>
                        <p className="text-xs text-amber-700">Earned: {leaveBalance.comp_off_earned}</p>
                        <p className="text-xs text-amber-700">Based on approved attendance on off-days</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Leave Application Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Date Range Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Calendar className="inline h-4 w-4 mr-1" />
                      Start Date
                    </label>
                    <input
                      type="date"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Calendar className="inline h-4 w-4 mr-1" />
                      End Date
                    </label>
                    <input
                      type="date"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleChange}
                      min={formData.startDate}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                {/* Leave Days Configuration */}
                {leaveDays.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Configure Leave Days ({leaveDays.length} day{leaveDays.length !== 1 ? 's' : ''})
                    </label>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3 max-h-96 overflow-y-auto">
                      {leaveDays.map((day, index) => (
                        <div key={index} className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Date Display */}
                            <div className="flex items-center">
                              <Calendar className="h-5 w-5 text-gray-500 mr-2" />
                              <div>
                                <p className="text-xs text-gray-500">Date</p>
                                <p className="text-sm font-semibold text-gray-900">
                                  {new Date(day.date).toLocaleDateString('en-US', {
                                    weekday: 'short',
                                    month: 'short',
                                    day: 'numeric'
                                  })}
                                </p>
                              </div>
                            </div>

                            {/* Day Type Toggle */}
                            <div>
                              <p className="text-xs text-gray-500 mb-2">Day Type</p>
                              <div className="flex space-x-2">
                                <button
                                  type="button"
                                  onClick={() => handleDayTypeChange(index, 'full')}
                                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                    day.dayType === 'full'
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                  }`}
                                >
                                  Full Day
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDayTypeChange(index, 'half')}
                                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                    day.dayType === 'half'
                                      ? 'bg-yellow-600 text-white'
                                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                  }`}
                                >
                                  Half Day
                                </button>
                              </div>
                            </div>

                            {/* Leave Type Selector */}
                            <div>
                              <p className="text-xs text-gray-500 mb-2">Leave Type</p>
                              <div className="grid grid-cols-2 gap-2">
                                {!isOnProbation && (
                                  <button
                                    type="button"
                                    onClick={() => handleLeaveTypeChange(index, 'earned')}
                                    className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center justify-center ${
                                      day.leaveType === 'earned'
                                        ? 'bg-green-600 text-white'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                                  >
                                    <Umbrella className="h-4 w-4 mr-1" />
                                    Earned
                                  </button>
                                )}
                                {leaveBalance?.comp_off_left > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => handleLeaveTypeChange(index, 'comp_off')}
                                    className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center justify-center ${
                                      day.leaveType === 'comp_off'
                                        ? 'bg-amber-600 text-white'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                                  >
                                    <Gift className="h-4 w-4 mr-1" />
                                    Comp Off
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => handleLeaveTypeChange(index, 'sick')}
                                  className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center justify-center ${
                                    day.leaveType === 'sick'
                                      ? 'bg-pink-600 text-white'
                                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                  }`}
                                >
                                  <Heart className="h-4 w-4 mr-1" />
                                  Sick
                                </button>
                                {isSSBranch && !isOnProbation && (
                                  <button
                                    type="button"
                                    onClick={() => handleLeaveTypeChange(index, 'casual')}
                                    className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center justify-center ${
                                      day.leaveType === 'casual'
                                        ? 'bg-purple-600 text-white'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                                  >
                                    <Calendar className="h-4 w-4 mr-1" />
                                    Casual
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => handleLeaveTypeChange(index, 'without_pay')}
                                  className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center justify-center ${
                                    day.leaveType === 'without_pay'
                                      ? 'bg-orange-600 text-white'
                                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                  }`}
                                >
                                  <X className="h-4 w-4 mr-1" />
                                  W/O Pay
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Summary */}
                    <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-blue-900 mb-2">Leave Summary</h4>
                      <div className={`grid grid-cols-2 gap-4 ${isSSBranch ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
                        <div className="flex items-center space-x-2">
                          <Umbrella className="h-5 w-5 text-green-600" />
                          <div>
                            <p className="text-xs text-gray-600">Earned</p>
                            <p className="text-lg font-bold text-gray-900">{earnedCount}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Heart className="h-5 w-5 text-pink-600" />
                          <div>
                            <p className="text-xs text-gray-600">Sick</p>
                            <p className="text-lg font-bold text-gray-900">{sickCount}</p>
                          </div>
                        </div>
                        {isSSBranch && (
                          <div className="flex items-center space-x-2">
                            <Calendar className="h-5 w-5 text-purple-600" />
                            <div>
                              <p className="text-xs text-gray-600">Casual</p>
                              <p className="text-lg font-bold text-gray-900">{casualCount}</p>
                            </div>
                          </div>
                        )}
                        <div className="flex items-center space-x-2">
                          <Gift className="h-5 w-5 text-amber-600" />
                          <div>
                            <p className="text-xs text-gray-600">Comp Off</p>
                            <p className="text-lg font-bold text-gray-900">{compOffCount}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <X className="h-5 w-5 text-orange-600" />
                          <div>
                            <p className="text-xs text-gray-600">W/O Pay</p>
                            <p className="text-lg font-bold text-gray-900">{withoutPayCount}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Reason */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FileText className="inline h-4 w-4 mr-1" />
                    Reason (minimum 10 characters)
                  </label>
                  <textarea
                    name="reason"
                    value={formData.reason}
                    onChange={handleChange}
                    rows="4"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Please provide a reason for your leave..."
                    required
                    minLength="10"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.reason.length}/10 characters minimum
                  </p>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading || leaveDays.length === 0}
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <FileText className="h-5 w-5" />
                      <span>Submit Leave Application</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaveApplication;
